import { useCallback } from "react";

import { useI18n } from "@/lib/i18n/i18n_provider";
import { useAuth0 } from "./auth0";
import { refusedPermission } from "./permissions";

/**
 * What to tell the user when the API refuses them for a permission they lack:
 * which permission, by the name the Team page shows it under, and who can
 * grant it. The fetchers put this in place of the API's own sentence, so every
 * toast says the same thing whichever endpoint refused. Answers nothing for
 * any other failure, and for a key the profile's registry does not name, so
 * the caller falls back to the API's message.
 */
export function usePermissionRefusalMessage(): (
  status: number,
  body: unknown
) => string | undefined {
  const { userProfile } = useAuth0();
  const { fbt } = useI18n();
  const registry = userProfile?.permissionRegistry;

  return useCallback(
    (status: number, body: unknown) => {
      const key = refusedPermission(status, body);
      if (!key) return undefined;
      const label = registry?.find((entry) => entry.key === key)?.label;
      if (!label) return undefined;
      return fbt(
        "You need the “" +
          fbt.param("permission", fbt(label)) +
          "” permission to do this. Ask an owner or admin on your team to grant it."
      );
    },
    [registry, fbt]
  );
}
